import { LinkedinIcon } from '@/app/icons/LinkedinIcon'
import { MydraIcon } from '@/app/icons/MydraIcon'
import { EXTERNAL_LINKS, WEBSITE_LINKS } from '@/app/lib/constants'
import { Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import React from 'react'

type FooterProps = React.ComponentProps<'footer'>

const linkClassName =
  'text-base font-medium text-white transition-opacity hover:opacity-80'

export function Footer({ ...props }: FooterProps) {
  const t = useTranslations('Footer')
  const year = new Date().getFullYear()

  return (
    <footer
      {...props}
      className={`mt-auto bg-mydra-black text-white ${props.className ?? ''}`}
    >
      <div className="container flex flex-col gap-6 px-4 pb-8 pt-12 md:pb-10 md:pt-16">
        <div className="flex flex-col justify-between gap-10 md:flex-row md:items-start md:gap-16">
          <div className="flex max-w-md flex-col gap-5">
            <MydraIcon
              aria-hidden
              className="h-8 w-8 shrink-0 [&_path]:fill-white"
            />
            <div className="flex flex-col gap-2 text-body2 leading-snug text-white">
              <Typography variant='body2' >{t('tagline_primary')}</Typography>
              <Typography variant='body2' >{t('tagline_secondary')}</Typography>
            </div>
            <Link
              href={EXTERNAL_LINKS.linkedIn}
              target="_blank"
              rel="noopener noreferrer"
              className=""
              aria-label="LinkedIn"
            >
              <LinkedinIcon className="h-6 w-6   [&_path]:fill-white" />
            </Link>
          </div>

          <nav
            className="flex flex-row flex-nowrap gap-x-12 md:justify-end md:gap-x-16"
            aria-label={t('footer_navigation')}
          >
            <ul className="flex min-w-[10rem] flex-col gap-3.5">
              <li>
                <Link className={linkClassName} href={WEBSITE_LINKS.help}>
                  <Typography variant='body4' >{t('contact_us')}</Typography>
                </Link>
              </li>
              <li>
                <Link
                  className={linkClassName}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={WEBSITE_LINKS.paymentOptions}
                >
                  <Typography variant='body4'>{t('payment_options')}</Typography>
                </Link>
              </li>
              <li>
                <Link
                  className={linkClassName}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={WEBSITE_LINKS.faqs}
                >
                  <Typography variant='body4'>{t('help_center')}</Typography>
                </Link>
              </li>
            </ul>
            <ul className="flex min-w-[10rem] flex-col gap-3.5">
              <li>
                <Link
                  className={linkClassName}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={WEBSITE_LINKS.privacyPolicy}
                >
                  <Typography variant='body4'>{t('privacy')}</Typography>
                </Link>
              </li>
              <li>
                <Link
                  className={linkClassName}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={WEBSITE_LINKS.termsAndConditions}
                >
                  <Typography variant='body4'>{t('terms_of_use')}</Typography>
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  data-cc="show-preferencesModal"
                >
                  <Typography variant='body4'>{t('cookies')}</Typography>
                </button>
              </li>
            </ul>
          </nav>
        </div>
        <hr className="border-0 border-t border-[#333333]" />
        <p className="text-sm text-white">
          {t('copyright_notice', { year })}
        </p>
      </div>
    </footer>
  )
}
